import { Progress, Tooltip } from 'antd';
import { useCallback, useMemo } from 'react';
import { SprintIcon } from '../../../assets/CustomIcons/SprintIcon';
import { TriangleIcon } from '../../../assets/CustomIcons/TriangleIcon';
import PlanningUserSvg from '../../../assets/icons/planning-user.svg?react';
import colorTokens from '../../../constants/design_system/colors';
import { Sprint } from '../../../types/Sprint';
import Task from '../../../types/Task';
import { TaskStatus } from '../../../types/TaskStatus';
import { WorkspaceEstimationType } from '../../../types/Workspace';
import { calculateSprintMetrics } from '../../../utils/sprintMetrics';
import { selectSprintCountableTasks } from '../../../utils/taskListUtils';
import { SprintNameTitleInput } from '../../Planning/Input/SprintNameTitleInput';
import { BaseButton } from '../../UI/BaseButton';

// Constants
const SPRINT_THRESHOLDS = {
  WARNING: 14,
  DANGER: 21,
} as const;

const SPRINT_STATES = {
  DEFAULT: 'default',
  WARNING: 'warning',
  DANGER: 'danger',
} as const;

// Type for sprint state
type SprintState = (typeof SPRINT_STATES)[keyof typeof SPRINT_STATES];

interface SprintInfo {
  label: string;
  state: SprintState;
  labelColor: string;
  duration: number;
}

interface SprintHeaderProps {
  sprint: Sprint;
  dataSource: Task[];
  statusOptions: TaskStatus[];
  estimationType?: WorkspaceEstimationType;
  endSprint: () => void;
}

// Utility function for date calculations
const calculateSprintDuration = (startDate: string): number => {
  try {
    const start = new Date(startDate);
    const now = new Date();

    // Validate date
    if (isNaN(start.getTime())) {
      console.warn('Invalid sprint start date:', startDate);
      return 0;
    }

    const diffTime = Math.abs(now.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return Math.max(1, diffDays); // Minimum 1 day
  } catch (error) {
    console.error('Error calculating sprint duration:', error);
    return 0;
  }
};

// Utility function for sprint info
const getSprintInfo = (duration: number): SprintInfo => {
  if (duration <= SPRINT_THRESHOLDS.WARNING) {
    return {
      label: `Day ${duration}`,
      state: SPRINT_STATES.DEFAULT,
      labelColor: 'bg-blue-100 text-blue-600',
      duration,
    };
  }

  if (duration <= SPRINT_THRESHOLDS.DANGER) {
    return {
      label: `Day ${duration}`,
      state: SPRINT_STATES.WARNING,
      labelColor: 'bg-yellow-200 text-yellow-600',
      duration,
    };
  }

  return {
    label: `Day ${duration}`,
    state: SPRINT_STATES.DANGER,
    labelColor: 'bg-red-100 text-red-600',
    duration,
  };
};

export const SprintHeader: React.FC<SprintHeaderProps> = ({
  sprint,
  dataSource,
  statusOptions,
  estimationType,
  endSprint,
}) => {
  // Every number in this header counts the same set of tasks, resolved through the
  // shared sprint predicate rather than the raw list — so the badge, the completed
  // ratio and the story points can't drift apart from each other or from the
  // End-Sprint modal. #2957
  const countableTasks = useMemo(() => selectSprintCountableTasks(dataSource, sprint?.id), [dataSource, sprint?.id]);

  // Calculate sprint metrics (single point of calculation)
  const sprintMetrics = useMemo(
    () => calculateSprintMetrics(countableTasks, statusOptions, estimationType),
    [countableTasks, statusOptions, estimationType]
  );

  // Memoize expensive calculations
  const sprintInfo = useMemo(() => {
    const duration = calculateSprintDuration(sprint.startDate);
    return getSprintInfo(duration);
  }, [sprint.startDate]);

  // Calculate task completion percentage
  const taskCompletionPercent = useMemo(() => {
    if (countableTasks.length === 0) return 0;
    return Math.round((sprintMetrics.completedTasksCount / countableTasks.length) * 100);
  }, [sprintMetrics.completedTasksCount, countableTasks.length]);

  // Memoize button props for performance
  const buttonProps = useMemo(
    () => ({
      type: sprintInfo.state === SPRINT_STATES.DANGER ? 'primary' : 'secondary',
      danger: sprintInfo.state !== SPRINT_STATES.DEFAULT,
      'aria-label': `End sprint (running for ${sprintInfo.duration} days)`,
    }),
    [sprintInfo.state, sprintInfo.duration]
  );

  // Memoize click handler
  const handleEndSprint = useCallback(() => {
    endSprint();
  }, [endSprint]);

  return (
    <div className="w-full flex justify-between items-center gap-1" role="banner" aria-label="Sprint header">
      <PlanningUserSvg aria-hidden="true" />

      <div className="text-brand-grey-dark text-xl font-semibold leading-7">Current Sprint:</div>

      <SprintNameTitleInput sprintId={sprint.id} sprintName={sprint.name} aria-label="Edit sprint name" />

      <div className="px-2 py-1 bg-yellow-200 rounded text-brand-grey-dark text-sm font-medium leading-tight">
        {countableTasks.length}
      </div>

      {/* Task completion progress */}
      <Tooltip title={<div style={{ whiteSpace: 'pre-line' }}>{sprintMetrics.taskProgressTooltip}</div>}>
        <div className="flex items-center gap-2 ml-auto mr-1">
          <Progress
            type="circle"
            percent={taskCompletionPercent}
            size={16}
            strokeWidth={12}
            strokeColor={colorTokens.taskonioGreen[600]}
            trailColor={colorTokens.neutral[600]}
            showInfo={false}
          />
          <span className="text-neutral-900 text-sm font-normal">
            {sprintMetrics.completedTasksCount}/{countableTasks.length}
          </span>
        </div>
      </Tooltip>

      {/* Story points progress - only show when estimationType is storyPoints */}
      {estimationType === WorkspaceEstimationType.storyPoints && (
        <Tooltip title={sprintMetrics.storyPointsTooltip}>
          <div className="flex items-center justify-center mr-1">
            <TriangleIcon className="text-neutral-900" />
            <span className="text-neutral-900 text-sm font-normal">
              {sprintMetrics.completedStoryPoints}/{sprintMetrics.totalStoryPoints}
            </span>
          </div>
        </Tooltip>
      )}

      <div
        className={`flex items-center h-6 gap-1 px-2 mr-2 rounded text-sm font-normal ${sprintInfo.labelColor}`}
        aria-label={`Sprint duration: ${sprintInfo.label}`}
        title={`Sprint has been running for ${sprintInfo.duration} day${sprintInfo.duration > 1 ? 's' : ''}`}
      >
        <SprintIcon aria-hidden="true" />
        {sprintInfo.label}
      </div>

      <div className="flex items-center mr-2">
        <BaseButton
          type={buttonProps.type as any}
          size="medium"
          danger={buttonProps.danger}
          onClick={handleEndSprint}
          aria-label={buttonProps['aria-label']}
        >
          <span className="whitespace-nowrap">End Sprint</span>
        </BaseButton>
      </div>
    </div>
  );
};
